/**
 * Sessao do accounts.
 *
 * Versao enxuta da do admin: aqui nao se faz login, 2FA nem recuperacao de
 * senha -- isso tudo continua no admin. O accounts so *recebe* um token
 * (ver services/sso.js), guarda, renova enquanto vale e descarta no logout.
 *
 * Duas diferencas deliberadas em relacao ao admin, ambas necessarias porque la
 * existe uma tela de login para onde cair e aqui nao:
 *
 *   - `refreshToken` relanca o erro em vez de engolir. Quem chama (o guard do
 *     router) e que decide para onde mandar o usuario; decidir aqui dentro
 *     produziria laco de redirecionamento, porque "/" no accounts e /users.
 *   - nem `logout` nem `refreshToken` tentam exibir mensagem traduzida. No
 *     admin esse trecho referencia um `t` que nao existe no modulo, entao o
 *     catch estoura em ReferenceError e mascara o erro real.
 */
import { apiRequest } from "@/services/api/apiClient.js";

/**
 * Troca o ticket do handoff pelo token desta aplicacao.
 *
 * Sem `Authorization`: nesta origem ainda nao ha sessao nenhuma, e quem
 * autentica a chamada e o proprio ticket -- de uso unico e vida curta. A
 * resposta traz o token ja com `aud` do accounts, as credenciais e o
 * `redirect_to` que o admin pediu.
 */
export const trocarTicket = async (ticket) => {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: "/sso/exchange",
        body: { ticket },
        sendToken: false,
    });
};

export const setAuth = ({ token, credentials }) => {
    if (token) {
        localStorage.setItem("authToken", token);
        localStorage.setItem("tokenExp", lerExpiracao(token));
    }

    if (credentials) {
        localStorage.setItem("credentials", JSON.stringify(credentials));
    }
};

export const hasSession = () => !!localStorage.getItem("authToken");

export const getToken = () => localStorage.getItem("authToken");

export const tokenNeedsRefresh = () => {
    const exp = localStorage.getItem("tokenExp");

    if (!exp) return true;

    return Date.now() >= Number(exp) * 1000 - 5 * 60 * 1000;
};

/**
 * Reconstroi o estado local da sessao a partir da API. Chamado depois do
 * handoff, quando so existe o token: usuario e corporacao vem do /me, nunca
 * do link.
 */
export const setUser = async () => {
    const response = await apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: "/me",
    });

    // `credentials.corp` e lido por getUser() e getCorp(); no admin ele vem da
    // resposta de login, que aqui nao existe -- a corporacao ativa chegou pelo
    // handoff e esta em localStorage['corp'].
    setAuth({
        credentials: {
            user: response.data,
            corp: localStorage.getItem("corp") ?? "",
        },
    });

    return response;
};

export const getUser = () => {
    const stored = localStorage.getItem("credentials");

    if (!stored) return null;

    const parsedWrapper = JSON.parse(stored);
    const parsed = parsedWrapper.user ?? parsedWrapper;

    const fullname = (parsed.name || "").trim();
    const parts = fullname.split(/\s+/).filter(Boolean);

    const corpId = parsedWrapper.corp ?? "";
    const corps = parsed.corps ?? {};

    let workgroups = [];

    for (const corpData of Object.values(corps)) {
        if (corpData.id === corpId) {
            const wg = corpData.workgroups ?? {};
            workgroups = Object.entries(wg).map(([name, id]) => ({ name, id }));
            break;
        }
    }

    return {
        id: parsed.id,
        name: parts[0] || "",
        fullname,
        lastName: parts.slice(1).join(" "),
        username: parsed.login,
        email: parsed.email,
        corpId,
        workgroups,
        fullUser: parsedWrapper,
    };
};

/** Invalida o token no servidor. Quem redireciona depois e o router. */
export const logout = async () => {
    try {
        await apiRequest({
            client: window.API_AUTH,
            method: "delete",
            url: "/logout",
            sendToken: true,
        });
    } finally {
        localStorage.clear();
    }
};

/** Renova o token. Lanca se nao der -- ver a nota no topo do arquivo. */
export const refreshToken = async () => {
    if (!getToken()) {
        throw new Error("Sem token para renovar.");
    }

    const response = await apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: "/refresh",
        sendToken: true,
    });

    setAuth({
        token: response.access_token,
        credentials: response.credentials,
    });

    return response;
};

function lerExpiracao(token) {
    const payloadBase64 = token.split(".")[1];
    const base64 = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");

    const json = decodeURIComponent(
        atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
    );

    return JSON.parse(json).exp;
}
